import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogIn, AlertCircle } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { useLanguage, langPrefix, getLangFromPath } from "@/i18n/LanguageContext";
import { useUserAuth, buildGoogleRedirect } from "@/user/UserAuthContext";
import LocalizedLink from "@/components/LocalizedLink";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AUTH_STRINGS } from "@/user/authStrings";

/**
 * Public sign-in page (P1 hardening).
 *
 * Failure model — every layer is defensive so NOTHING here can unmount
 * the React tree:
 *   1. Client-side schema validation (zod) runs BEFORE any network call.
 *      Empty / malformed input never leaves the browser; each field gets an
 *      inline, screen-reader-announced error.
 *   2. `login()` (UserAuthContext) never rejects: network failures and every
 *      4xx/5xx are normalised to `{ ok:false, code, error:string }`.
 *   3. The submit handler still wraps the call in try/catch/finally so an
 *      unexpected throw is surfaced as a friendly alert and the pending
 *      state is ALWAYS released (no stuck "Signing in…" button).
 *   4. Error text is coerced to a string before rendering — the original
 *      crash was a Pydantic 422 `detail` ARRAY rendered as a React child.
 *   5. The whole card sits inside a section-scoped <ErrorBoundary/> so even
 *      a render-time bug degrades to an inline fallback, keeping the
 *      navbar / footer alive.
 */

// Messages are KEYS into AUTH_STRINGS so the same schema serves AR + EN.
const LoginSchema = z.object({
  email: z.string().trim().min(1, "emailRequired").email("emailInvalid"),
  password: z.string().min(1, "passwordRequired"),
});

/** Turn a zod result into `{ field: localisedMessage }` (first issue wins). */
export function zodFieldErrors(result, L) {
  if (result.success) return {};
  const out = {};
  for (const issue of result.error.issues) {
    const field = String(issue.path[0] || "form");
    if (out[field]) continue;
    out[field] = L[issue.message] || L.errValidation;
  }
  return out;
}

/** Map a normalised auth failure to a localised, user-friendly sentence. */
export function authErrorMessage(L, res) {
  const code = res && res.code;
  switch (code) {
    case "invalid_credentials":
      return L.errInvalidCredentials;
    case "validation":
      return L.errValidation;
    case "conflict":
      return L.errConflict;
    case "rate_limited":
      return L.errRateLimited;
    case "network":
      return L.errNetwork;
    case "server":
      return L.errServer;
    default:
      return res && typeof res.error === "string" && res.error.trim()
        ? res.error
        : L.errGeneric;
  }
}

/** Codes worth an additional toast (transient infrastructure problems). */
const TOAST_CODES = new Set(["network", "server", "rate_limited"]);

export default function AuthLogin() {
  return (
    <ErrorBoundary scope="section">
      <LoginForm />
    </ErrorBoundary>
  );
}

function LoginForm() {
  const { lang } = useLanguage();
  const L = AUTH_STRINGS[lang] || AUTH_STRINGS.en;
  const { login, status } = useUserAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectTo = useMemo(() => {
    const from = location.state?.from;
    // Only honour `from` when it lives in the active language tree.
    if (from && typeof from === "string" && getLangFromPath(from) === lang) {
      return from;
    }
    return `${langPrefix(lang)}/account` || "/account";
  }, [lang, location.state]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Surface backend-redirect Google errors arriving as ?google_error=...
  const googleError = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const code = params.get("google_error");
    if (!code) return null;
    if (code === "unavailable") return L.googleErrorUnavailable;
    if (code === "access_denied") return L.googleErrorAccessDenied;
    if (code === "no_email") return L.googleErrorNoEmail;
    return L.googleErrorGeneric;
  }, [location.search, L]);

  // Already authenticated → leave. Done in an effect (never during render).
  useEffect(() => {
    if (status === "authenticated") navigate(redirectTo, { replace: true });
  }, [status, redirectTo, navigate]);

  const clearFieldError = (name) => {
    if (!fieldErrors[name]) return;
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setFormError(null);

    // 1) Schema validation — no request for invalid input.
    const parsed = LoginSchema.safeParse({ email, password });
    const errors = zodFieldErrors(parsed, L);
    setFieldErrors(errors);
    if (!parsed.success) {
      setFormError(L.formHasErrors);
      return;
    }

    // 2) Dispatch with the submit trigger disabled while in flight.
    setSubmitting(true);
    try {
      const res = await login(parsed.data.email, parsed.data.password);
      if (!res || !res.ok) {
        const message = authErrorMessage(L, res);
        setFormError(message);
        if (res && TOAST_CODES.has(res.code)) toast.error(message);
        return;
      }
      navigate(redirectTo, { replace: true });
    } catch (err) {
      // Belt-and-braces: `login()` resolves on every known path, but an
      // unexpected throw must still become a friendly message.
      // eslint-disable-next-line no-console
      console.error("[AuthLogin] unexpected failure", err);
      setFormError(L.errGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  const formErrorText =
    typeof formError === "string" ? formError : formError ? L.errGeneric : null;

  return (
    <AuthCard eyebrow={L.eyebrow} title={L.loginTitle} body={L.loginBody}>
      {googleError && (
        <FormAlert id="login-google-error" testId="login-google-error">
          {googleError}
        </FormAlert>
      )}

      <form
        onSubmit={onSubmit}
        className="mt-6 space-y-4"
        data-testid="login-form"
        noValidate
        aria-busy={submitting || undefined}
        aria-describedby={formErrorText ? "login-form-error" : undefined}
      >
        <Field
          label={L.email}
          htmlFor="login-email"
          error={fieldErrors.email}
          errorId="login-email-error"
          errorTestId="login-email-error"
        >
          <input
            id="login-email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearFieldError("email");
            }}
            className="sas-input"
            data-testid="login-email"
            dir="ltr"
            aria-invalid={fieldErrors.email ? "true" : undefined}
            aria-describedby={fieldErrors.email ? "login-email-error" : undefined}
            disabled={submitting}
          />
        </Field>
        <Field
          label={L.password}
          htmlFor="login-password"
          error={fieldErrors.password}
          errorId="login-password-error"
          errorTestId="login-password-error"
        >
          <input
            id="login-password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearFieldError("password");
            }}
            className="sas-input"
            data-testid="login-password"
            dir="ltr"
            aria-invalid={fieldErrors.password ? "true" : undefined}
            aria-describedby={fieldErrors.password ? "login-password-error" : undefined}
            disabled={submitting}
          />
        </Field>

        {formErrorText && (
          <FormAlert id="login-form-error" testId="login-error">
            {formErrorText}
          </FormAlert>
        )}

        <button
          type="submit"
          disabled={submitting}
          aria-disabled={submitting || undefined}
          className="sas-btn sas-btn-primary w-full justify-center disabled:opacity-70 disabled:cursor-not-allowed"
          data-testid="login-submit"
        >
          <LogIn size={15} aria-hidden />
          {submitting ? L.loginLoading : L.loginCta}
        </button>
      </form>

      <OrDivider label={L.or} />

      <GoogleButton lang={lang} label={L.continueGoogle} testId="login-google" />

      <div className="mt-6 text-center text-[13px] text-[color:var(--sas-ink-2)]">
        {L.noAccount}{" "}
        <LocalizedLink
          to="/auth/signup"
          className="text-[color:var(--sas-accent)] font-medium hover:underline"
          data-testid="login-go-signup"
        >
          {L.createOne}
        </LocalizedLink>
      </div>
      <BackLink lang={lang} label={L.backToSite} />
    </AuthCard>
  );
}

// --- Reusable primitives used across auth pages -----------------------------

export function AuthCard({ eyebrow, title, body, children }) {
  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-start md:items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-[color:var(--sas-line)] shadow-sm p-7 sm:p-8">
          {eyebrow && <div className="sas-eyebrow mb-2">{eyebrow}</div>}
          {title && <h1 className="sas-h2" data-testid="auth-card-title">{title}</h1>}
          {body && <p className="sas-body mt-3 text-[14.5px]">{body}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Labelled field with an optional inline error.
 * `error` is coerced to a string so a non-string value can never crash the
 * tree. Backwards compatible: callers that only pass `label` + children
 * (onboarding page) render exactly as before.
 */
export function Field({ label, htmlFor, error, errorId, errorTestId, children }) {
  const errorText = error == null ? null : String(error);
  const Wrapper = htmlFor ? "div" : "label";
  return (
    <Wrapper className="block">
      {htmlFor ? (
        <label
          htmlFor={htmlFor}
          className="block text-[12.5px] font-medium text-[color:var(--sas-ink)] mb-1.5"
        >
          {label}
        </label>
      ) : (
        <span className="block text-[12.5px] font-medium text-[color:var(--sas-ink)] mb-1.5">
          {label}
        </span>
      )}
      {children}
      {errorText && (
        <span
          id={errorId}
          role="alert"
          className="mt-1.5 flex items-start gap-1.5 text-[12.5px] leading-snug text-[color:var(--sas-danger,#b42318)]"
          data-testid={errorTestId}
        >
          <AlertCircle size={13} className="mt-[2px] shrink-0" aria-hidden />
          <span>{errorText}</span>
        </span>
      )}
    </Wrapper>
  );
}

/** Form-level alert (server / network / validation summary). */
export function FormAlert({ id, testId, children }) {
  const text = typeof children === "string" ? children : String(children ?? "");
  return (
    <div
      id={id}
      role="alert"
      aria-live="assertive"
      className="rounded-lg border border-[color:var(--sas-danger-line,#fecaca)] bg-[color:var(--sas-danger-soft,#fef2f2)] px-3 py-2.5 text-[13px] text-[color:var(--sas-danger,#991b1b)] flex items-start gap-2"
      data-testid={testId}
    >
      <AlertCircle size={15} className="mt-[1px] shrink-0" aria-hidden />
      <span>{text}</span>
    </div>
  );
}

export function OrDivider({ label }) {
  return (
    <div className="flex items-center gap-3 my-5" aria-hidden>
      <span className="flex-1 h-px bg-[color:var(--sas-line)]" />
      <span className="text-[11.5px] uppercase tracking-wide text-[color:var(--sas-ink-2)]">
        {label}
      </span>
      <span className="flex-1 h-px bg-[color:var(--sas-line)]" />
    </div>
  );
}

export function GoogleButton({ lang, label, testId }) {
  const onClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = buildGoogleRedirect(lang);
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full inline-flex items-center justify-center gap-2.5 h-11 rounded-full border border-[color:var(--sas-line)] bg-white hover:border-[color:var(--sas-ink)] transition-colors text-[14px] font-medium text-[color:var(--sas-ink)]"
      data-testid={testId}
    >
      <GoogleG />
      <span>{label}</span>
    </button>
  );
}

function GoogleG() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  );
}

export function BackLink({ lang, label }) {
  const home = lang === "ar" ? "/ar" : "/";
  return (
    <div className="text-center mt-6">
      <a
        href={home}
        className="text-xs text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)]"
        data-testid="auth-back-to-site"
      >
        {label}
      </a>
    </div>
  );
}
